
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Zap, Clock, Download, Share2, Target, Calculator, Settings } from "lucide-react";
import { type CaixaLotteryType, CAIXA_LOTTERIES, type ClosureType } from "@/utils/caixa";
import { type SubscriptionPlan } from "@/utils/predictionUtils";
import CaixaPredictionCard from "./CaixaPredictionCard";
import ClosureSelector from "./ClosureSelector";
import FixedNumbersSelector from "./FixedNumbersSelector";
import CostCalculator from "./CostCalculator";

interface LoteriasContentProps {
  lotteryType: CaixaLotteryType;
  predictions: Array<{ value: string; numbers: string[] }>;
  userPlan: SubscriptionPlan;
  isGenerating: boolean;
  selectedAI: "gemini" | "chatgpt" | "claude";
  gameCount: number;
  showLastUpdate: boolean;
  setShowLastUpdate: (show: boolean) => void;
  onGeneratePredictions: () => void;
  onShare: () => void;
}

const LoteriasContent: React.FC<LoteriasContentProps> = ({
  lotteryType,
  predictions,
  userPlan,
  isGenerating,
  selectedAI,
  gameCount,
  showLastUpdate,
  setShowLastUpdate,
  onGeneratePredictions,
  onShare,
}) => {
  const [selectedClosure, setSelectedClosure] = useState<string>("");
  const [fixedNumbers, setFixedNumbers] = useState<number[]>([]);
  const [closureGames, setClosureGames] = useState<Array<{ numbers: string[] }>>([]);
  const [selectedClosureInfo, setSelectedClosureInfo] = useState<ClosureType | null>(null);

  const lottery = CAIXA_LOTTERIES[lotteryType];
  const hasGeneratedPredictions = predictions && predictions.length > 0;
  const hasClosureGames = closureGames && closureGames.length > 0;

  const handleClosureSelect = (closure: ClosureType, games: Array<{ numbers: string[] }>, fixedNumbers?: number[]) => {
    console.log("Closure selected:", closure, "Games:", games, "Fixed numbers:", fixedNumbers);
    setSelectedClosure(closure.id);
    setClosureGames(games);
    setSelectedClosureInfo(closure);
    if (fixedNumbers) {
      setFixedNumbers(fixedNumbers);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
      <div className="space-y-6 sm:space-y-8">
        {/* Lottery Info Card */}
        <Card className="w-full bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl text-center text-blue-800">
              {lottery.icon} {lottery.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-700 text-sm sm:text-base">{lottery.description}</p>
            
            {/* Info Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="font-bold text-blue-600">{lottery.numbersRange}</div>
                <div className="text-gray-600 text-xs">Números</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="font-bold text-green-600">{lottery.numbersToSelect}</div>
                <div className="text-gray-600 text-xs">Escolher</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="font-bold text-purple-600">{lottery.minPrice}</div>
                <div className="text-gray-600 text-xs">Preço Mín.</div>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm col-span-2 lg:col-span-1">
                <div className="font-bold text-red-600 text-xs sm:text-sm">{lottery.drawDays.join(", ")}</div>
                <div className="text-gray-600 text-xs">Sorteios</div>
              </div>
            </div>
            
            {/* Generate Button */}
            <div className="pt-4 space-y-4">
              <Button
                onClick={onGeneratePredictions}
                disabled={isGenerating}
                size="lg"
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                {isGenerating ? (
                  <>
                    <Zap className="mr-2 h-5 w-5 animate-spin" />
                    Gerando Palpites...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Gerar Palpites com IA
                  </>
                )}
              </Button>

              {/* Navigation Buttons - Mobile First */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => scrollToSection('fechamentos')}
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline">Fechamentos</span>
                  <span className="xs:hidden">Fecha.</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => scrollToSection('numeros-fixos')}
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline">Números Fixos</span>
                  <span className="xs:hidden">Fixos</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => scrollToSection('calculadora')}
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                >
                  <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline">Calculadora</span>
                  <span className="xs:hidden">Calc.</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Predictions Display - Mantém o local original */}
        {hasGeneratedPredictions && (
          <Card className="w-full bg-gradient-to-br from-green-50 to-blue-50 border-green-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <CardTitle className="text-lg sm:text-xl text-green-800">
                  🎯 Palpites Gerados com IA - {lottery.name}
                </CardTitle>
                <div className="flex gap-2 justify-center sm:justify-end">
                  <Button variant="outline" size="sm" onClick={onShare}>
                    <Share2 className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Compartilhar</span>
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Baixar</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {showLastUpdate && (
                <div className="mb-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 text-sm">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1">
                      Palpites gerados com {selectedAI === "gemini" ? "Gemini" : selectedAI === "claude" ? "Claude.ai" : "ChatGPT"} • {new Date().toLocaleString('pt-BR')}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowLastUpdate(false)}
                      className="ml-auto text-blue-600 hover:text-blue-800 p-1"
                    >
                      ×
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center">
                {predictions.slice(0, userPlan === "free" ? 3 : gameCount).map((prediction, index) => (
                  <CaixaPredictionCard
                    key={index}
                    numbers={prediction.numbers}
                    index={index}
                    lotteryType={lotteryType}
                  />
                ))}
              </div>
              
              {userPlan === "free" && predictions.length > 3 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-yellow-800 font-medium text-sm sm:text-base">
                    🔒 Mais {predictions.length - 3} palpites disponíveis no plano premium
                  </p>
                  <Button size="sm" className="mt-2">
                    Fazer Upgrade
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Advanced Tools Tabs */}
        <div className="w-full">
          <Tabs defaultValue="fechamentos" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="fechamentos" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Fechamentos</span>
                <span className="sm:hidden">Fecha.</span>
              </TabsTrigger>
              <TabsTrigger value="fixos" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Números Fixos</span>
                <span className="sm:hidden">Fixos</span>
              </TabsTrigger>
              <TabsTrigger value="calculadora" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Calculadora</span>
                <span className="sm:hidden">Calc.</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="fechamentos" className="space-y-4" id="fechamentos">
              <ClosureSelector
                onClosureSelect={handleClosureSelect}
                lotteryType={lotteryType}
                selectedClosure={selectedClosure}
                onClosureChange={setSelectedClosure}
                userPlan={userPlan}
              />
              
              {/* Closure Games Display - Movido para dentro da aba de fechamentos */}
              {hasClosureGames && selectedClosureInfo && (
                <Card className="w-full bg-gradient-to-br from-gold-50 to-yellow-50 border-gold-200 mt-6">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                      <CardTitle className="text-lg sm:text-xl text-gold-800">
                        🎯 Jogos do Fechamento: {selectedClosureInfo.name}
                      </CardTitle>
                      <div className="flex gap-2 justify-center sm:justify-end">
                        <Button variant="outline" size="sm" onClick={onShare}>
                          <Share2 className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Compartilhar</span>
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Baixar</span>
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gold-600">
                      {selectedClosureInfo.description} • Garantia: {selectedClosureInfo.guarantee}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center">
                      {closureGames.map((game, index) => (
                        <CaixaPredictionCard
                          key={`closure-${index}`}
                          numbers={game.numbers}
                          index={index}
                          lotteryType={lotteryType}
                        />
                      ))}
                    </div>
                    
                    {fixedNumbers.length > 0 && (
                      <div className="mt-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                        <div className="text-blue-700 text-sm">
                          <span className="font-medium">Números Fixos:</span> {fixedNumbers.map(num => num.toString().padStart(2, '0')).join(', ')}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="fixos" className="space-y-4" id="numeros-fixos">
              <FixedNumbersSelector
                selectedNumbers={fixedNumbers}
                onFixedNumbersChange={setFixedNumbers}
                lotteryType={lotteryType}
                maxFixedNumbers={6}
              />
            </TabsContent>

            <TabsContent value="calculadora" className="space-y-4" id="calculadora">
              <CostCalculator
                lotteryType={lotteryType}
                gamesCount={gameCount}
                userPlan={userPlan}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default LoteriasContent;
